import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { SUBSCRIPTION_PLANS, type PlanType } from "@/lib/subscriptionConfig";

export const SubscriptionPaywall = () => {
  const { session, subscription, checkSubscription } = useAuth();
  const [loading, setLoading] = useState<PlanType | null>(null);
  const { toast } = useToast();

  const handleSubscribe = async (plan: PlanType) => {
    if (!session) return;

    setLoading(plan);
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { priceId: SUBSCRIPTION_PLANS[plan].priceId },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;

      if (data?.url) {
        window.open(data.url, '_blank');
        // Check subscription status after a delay
        setTimeout(() => checkSubscription(), 3000);
      }
    } catch (error) {
      console.error('Subscription error:', error);
      toast({
        title: "Error",
        description: "Failed to start subscription. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(null);
    }
  };

  const handleManageSubscription = async () => {
    if (!session) return;

    setLoading('monthly');
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;

      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Portal error:', error);
      toast({
        title: "Error",
        description: "Failed to open customer portal. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-b from-background to-secondary/20">
      <div className="max-w-4xl w-full space-y-8">
        <div className="text-center space-y-2">
          <h1 className="text-4xl font-bold">Choose Your Plan</h1>
          <p className="text-muted-foreground text-lg">
            Start your 3-day free trial. Cancel anytime.
          </p>
          {subscription?.onTrial && (
            <Badge variant="secondary" className="text-base px-4 py-1">
              Free Trial Active
            </Badge>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {(Object.keys(SUBSCRIPTION_PLANS) as PlanType[]).map((planKey) => {
            const plan = SUBSCRIPTION_PLANS[planKey];
            const isYearly = planKey === 'yearly';

            return (
              <Card key={planKey} className={isYearly ? "border-primary shadow-lg" : ""}>
                <CardHeader>
                  {isYearly && (
                    <Badge className="w-fit mb-2">Best Value</Badge>
                  )}
                  <CardTitle className="text-2xl">{plan.name}</CardTitle>
                  <CardDescription>{plan.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-1">
                    <div className="text-4xl font-bold">{plan.price}</div>
                    <div className="text-sm text-muted-foreground">per {plan.interval}</div>
                  </div>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-primary" />
                      <span className="text-sm">AI-powered task suggestions</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-primary" />
                      <span className="text-sm">Personalized coaching</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-primary" />
                      <span className="text-sm">Progress tracking</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-primary" />
                      <span className="text-sm">Achievement system</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-primary" />
                      <span className="text-sm">3-day free trial</span>
                    </li>
                  </ul>
                </CardContent>
                <CardFooter>
                  <Button
                    className="w-full"
                    size="lg"
                    onClick={() => handleSubscribe(planKey)}
                    disabled={loading !== null}
                    variant={isYearly ? "default" : "outline"}
                  >
                    {loading === planKey ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      "Start Free Trial"
                    )}
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>

        {subscription?.subscribed && (
          <div className="text-center space-y-4">
            <Card className="border-primary">
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground mb-4">
                  You have an active subscription
                  {subscription.onTrial && " (Free Trial)"}
                </p>
                <Button
                  onClick={handleManageSubscription}
                  disabled={loading !== null}
                  variant="outline"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    "Manage Subscription"
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};
